import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, BookOpen, BarChart3, ClipboardList, Mail, Link2, Copy, CheckCircle2, RefreshCw, AlertTriangle, Users, GraduationCap, TrendingUp, Clock, Calendar, X, Pencil, PowerOff, RotateCcw, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useChamadas } from '../../api/hooks/useChamadas';
import { useCrud } from '../../api/hooks/useCrud';
import { useHorarioAtual } from '../../api/hooks/useHorarios';
import type { Professor, Materia } from '../../types/models';
import { useAuthStore } from '../../store/authStore';
import { hasPermission } from '../../auth/permissions';
import { formatCPF } from '../../utils/cpfUtils';
import './responsavel-ficha-desktop.css';
import '../../components/ui/datatable.css';
import { ROUTES } from '../../api/routes';
import { EQUIPE } from '../../api/routes';
import { normalizeActivationLink } from '../../components/invite';

const TABS = [
  { key: 'dados', label: 'Dados Pessoais', icon: User },
  { key: 'convite', label: 'Convite', icon: Mail },
  { key: 'aderencia', label: 'Chamadas', icon: ClipboardList },
];

export function ProfessorFichaFooter({
  professor,
  canManage,
  onEdit,
  onDeactivate,
}: {
  professor: { criadoEm?: string; ativo: boolean };
  canManage: boolean;
  onEdit: () => void;
  onDeactivate: () => void;
}) {
  return (
    <div className="resp-ficha-foot">
      <span className="resp-ficha-foot-meta">
        {professor.criadoEm
          ? `Cadastrado em ${new Date(professor.criadoEm).toLocaleDateString('pt-BR')}`
          : '—'}
      </span>
      <span className="resp-ficha-foot-spacer" />
      <div className="resp-ficha-foot-actions">
        {canManage && (
          <button type="button" className="resp-ficha-btn resp-ficha-btn--primary" onClick={onEdit}>
            <Pencil size={14} strokeWidth={2} />
            Editar
          </button>
        )}
        {professor.ativo ? (
          canManage && (
            <button type="button" className="resp-ficha-btn resp-ficha-btn--danger" onClick={onDeactivate}>
              <PowerOff size={14} strokeWidth={2} />
              Desativar
            </button>
          )
        ) : (
          canManage && (
            <button type="button" className="resp-ficha-btn resp-ficha-btn--success" onClick={onDeactivate}>
              <RotateCcw size={14} strokeWidth={2} />
              Reativar
            </button>
          )
        )}
      </div>
    </div>
  );
}

export function ProfessorDetalhePage({
  overrideId,
  onClose,
  onEdit,
  onDeactivate,
  layout = 'legacy',
}: {
  overrideId?: string;
  onClose?: () => void;
  onEdit?: () => void;
  onDeactivate?: () => void;
  layout?: 'legacy' | 'desktop';
} = {}) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = overrideId || routeId;
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const escolaId = user?.escolaId;
  const canManage = hasPermission(user, 'professores.manage');
  const [tab, setTab] = useState('dados');
  const [gerandoLink, setGerandoLink] = useState(false);
  const [enviandoRecuperacao, setEnviandoRecuperacao] = useState(false);
  /** Link devolvido pelo último POST /equipe/convidar (antes do refetch completar). */
  const [linkConviteOverride, setLinkConviteOverride] = useState<string | null>(null);
  const [pendingMateriaId, setPendingMateriaId] = useState<string | null>(null);
  const isModal = !!onClose;
  const desktopFicha = layout === 'desktop' && isModal;

  useEffect(() => {
    setLinkConviteOverride(null);
  }, [id]);
  const queryClient = useQueryClient();
  const { data: allMaterias } = useCrud<Materia>('materias');

  // Horarios cross-turma para mostrar turmas derivadas
  const { horariosHoje } = useHorarioAtual(id);
  const turmasProf = (() => {
    const map = new Map<string, { turmaId: string; turmaNome: string; aulas: number }>();
    (horariosHoje || []).forEach((h: any) => {
      if (h.turmaId && h.turma) {
        const existing = map.get(h.turmaId);
        if (existing) existing.aulas++;
        else map.set(h.turmaId, { turmaId: h.turmaId, turmaNome: h.turma.nome || h.turmaId, aulas: 1 });
      }
    });
    return Array.from(map.values());
  })();

  const { data: professor } = useQuery({
    queryKey: ['professor', escolaId, id],
    queryFn: async () => { const { data } = await api.get(ROUTES.professores.findOne(escolaId!, id!)); return data as Professor; },
    enabled: !!id && !!escolaId,
  });
  const { data: vinculosMaterias = [] } = useQuery({
    queryKey: ['professor-materias', escolaId, id],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.professores.materias(escolaId!, id!));
      return Array.isArray(data) ? data : [];
    },
    enabled: !!id && !!escolaId,
  });

  // Chamadas feitas pelo professor (últimos 30 dias)
  const { chamadas } = useChamadas({ professorId: id });

  if (!professor) {
    return <div className="admin-page" style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Carregando...</div>;
  }

  // Aderência: % de aulas com chamada registrada
  const chamadasConcluidas = chamadas.filter((c) => c.status === 'CONCLUIDA');
  const totalChamadas = chamadas.length;
  const pctAderencia = totalChamadas > 0 ? Math.round((chamadasConcluidas.length / totalChamadas) * 100) : 100;
  const aderenciaColor = pctAderencia >= 90 ? '#16a34a' : pctAderencia >= 70 ? '#d97706' : '#dc2626';

  const statusConta = String((professor as { statusConta?: string }).statusConta ?? '');
  const tokenAtivacao = (professor as { tokenAtivacao?: string }).tokenAtivacao;
  const codigoDisplay = (professor as { codigoDisplay?: string }).codigoDisplay;
  const tokenExpiraEmRaw = (professor as { tokenExpiraEm?: string }).tokenExpiraEm;
  const convitePendente =
    statusConta === 'PENDENTE' || statusConta === 'ATIVANDO' || statusConta === 'FALHOU';
  const activationLinkAtual =
    convitePendente && tokenAtivacao
      ? `${window.location.origin}/ativar?token=${encodeURIComponent(tokenAtivacao)}${codigoDisplay ? `&code=${encodeURIComponent(codigoDisplay)}` : ''}`
      : '';
  const linkConviteExibicao = linkConviteOverride || activationLinkAtual;
  const tokenExpiraFmt = (() => {
    if (!tokenExpiraEmRaw) return null;
    try {
      return new Date(tokenExpiraEmRaw).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return null;
    }
  })();
  const tokenExpiraMs = (() => {
    if (!tokenExpiraEmRaw) return null;
    const t = new Date(tokenExpiraEmRaw).getTime();
    return Number.isFinite(t) ? t : null;
  })();
  /** Override = link acabado de gerar (válido até o refetch trazer nova data). */
  const linkExpirado =
    convitePendente && !linkConviteOverride && tokenExpiraMs != null && Date.now() > tokenExpiraMs;
  const linkUtilParaEnvio = Boolean(linkConviteExibicao && !linkExpirado);

  const copiarLinkAtual = () => {
    if (!linkConviteExibicao) return;
    void navigator.clipboard.writeText(linkConviteExibicao);
    toast.success('Link copiado!');
  };

  const gerarNovoLink = async () => {
    if (!escolaId || !id) return;
    setGerandoLink(true);
    try {
      const { data } = await api.post<{ activationLink?: string }>(EQUIPE.convidar(escolaId), {
        nome: professor.nome,
        email: professor.email,
        cargo: 'PROFESSOR',
        telefone: professor.telefone || undefined,
      });
      if (data.activationLink) {
        const norm = normalizeActivationLink(data.activationLink);
        setLinkConviteOverride(norm);
        await navigator.clipboard.writeText(norm);
        toast.success('Link renovado, copiado e dados atualizados.');
      } else {
        toast.success('Convite atualizado.');
      }
      await queryClient.invalidateQueries({ queryKey: ['professor', escolaId, id] });
      setLinkConviteOverride(null);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || 'Erro ao gerar link');
    } finally {
      setGerandoLink(false);
    }
  };

  const abrirWhatsAppComLink = (link: string) => {
    const nome = professor.nome || 'Professor(a)';
    const msg = encodeURIComponent(
      `Olá ${nome}! Você foi convidado(a) como professor(a) no Aluno Conectado.\n\nAtive sua conta pelo link:\n${link}`,
    );
    const tel = professor.telefone?.replace(/\D/g, '');
    const url = tel ? `https://wa.me/${tel}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, '_blank');
  };

  const enviarRecuperacaoSenha = async () => {
    if (!professor.email || enviandoRecuperacao) return;
    setEnviandoRecuperacao(true);
    try {
      const { data } = await api.post<{ sent?: boolean; emailSent?: boolean }>('/auth/password-recovery/request', {
        email: professor.email,
      });
      if (data?.emailSent === false) {
        toast.warning('Solicitação registrada, mas o e-mail não pôde ser entregue agora.');
      } else {
        toast.success(`Link de recuperação enviado para ${professor.email}.`);
      }
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || 'Erro ao solicitar recuperação de senha.');
    } finally {
      setEnviandoRecuperacao(false);
    }
  };

  // Chamadas agrupadas por dia (últimas 10)
  const chamadasRecentes = chamadas.slice(0, 20);
  const hasFotoReal = (fotoUrl?: string | null) => {
    if (!fotoUrl || fotoUrl.length < 50) return false;
    const url = fotoUrl.toLowerCase();
    if (url.includes('i.pravatar.cc') || url.includes('ui-avatars.com')) return false;
    // Aceitar apenas data URIs (upload real) ou URLs internas longas
    if (!url.startsWith('data:image/') && !url.startsWith('/api/') && !url.startsWith('http')) return false;
    if (url.startsWith('http') && (url.includes('pravatar') || url.includes('avatar') || url.includes('ui-avatars') || url.includes('placeholder'))) return false;
    return true;
  };
  const materiasSelecionadas = new Set(
    (vinculosMaterias as any[]).map((v: any) => String(v?.materiaId)).filter(Boolean),
  );
  const materiasVisiveis = (() => {
    const byId = new Map<string, { id: string; nome: string; ativo: boolean }>();
    (allMaterias ?? []).forEach((m) => {
      if (!m.ativo) return;
      byId.set(m.id, { id: m.id, nome: m.nome, ativo: true });
    });
    (professor?.materias ?? []).forEach((m) => {
      if (!m?.id || !m?.nome) return;
      if (!byId.has(m.id)) byId.set(m.id, { id: m.id, nome: m.nome, ativo: false });
    });
    return Array.from(byId.values()).sort((a, b) => {
      const aSel = materiasSelecionadas.has(a.id);
      const bSel = materiasSelecionadas.has(b.id);
      if (aSel !== bSel) return aSel ? -1 : 1;
      if (a.ativo !== b.ativo) return a.ativo ? -1 : 1;
      return a.nome.localeCompare(b.nome, 'pt-BR');
    });
  })();
  const toggleMateria = async (materiaId: string) => {
    if (!escolaId || !id || pendingMateriaId) return;
    setPendingMateriaId(materiaId);
    try {
      const isSelected = materiasSelecionadas.has(materiaId);
      if (isSelected) {
        const vinculo = (vinculosMaterias as any[]).find((v: any) => String(v?.materiaId) === materiaId);
        if (!vinculo?.id) {
          toast.error('Vínculo não encontrado para remover');
          return;
        }
        await api.delete(ROUTES.professores.removerMateria(escolaId, id, vinculo.id));
        toast.success('Matéria desvinculada');
      } else {
        await api.post(ROUTES.professores.materias(escolaId!, id!), { materiaId });
        toast.success('Matéria vinculada');
      }
      queryClient.invalidateQueries({ queryKey: ['professor', escolaId, id] });
      queryClient.invalidateQueries({ queryKey: ['professor-materias', escolaId, id] });
    } catch {
      toast.error('Erro ao atualizar vínculo da matéria');
    } finally {
      setPendingMateriaId(null);
    }
  };

  const headerBlock = desktopFicha ? (
    <header className="resp-ficha-head">
      {hasFotoReal((professor as any).fotoUrl) ? (
        <img src={(professor as any).fotoUrl} alt="" className="resp-ficha-avatar" />
      ) : (
        <span className="resp-ficha-avatar-ph"><User size={32} /></span>
      )}
      <div className="resp-ficha-head-main">
        <p className="resp-ficha-eyebrow">Professor · ficha da escola</p>
        <h2 className="resp-ficha-name">{professor.nome}</h2>
        <div className="resp-ficha-meta-line">
          <span className="resp-ficha-sub">{professor.email}</span>
          <span className={`resp-ficha-badge ${professor.ativo ? 'resp-ficha-badge--ok' : 'resp-ficha-badge--off'}`}>
            {professor.ativo ? 'Ativo' : 'Inativo'}
          </span>
          <span className="resp-ficha-badge resp-ficha-badge--role" style={{ color: aderenciaColor, borderColor: `${aderenciaColor}33`, background: `${aderenciaColor}12` }}>
            {pctAderencia}% aderência
          </span>
        </div>
      </div>
      <button type="button" className="resp-ficha-close" onClick={onClose} aria-label="Fechar"><X size={16} /></button>
    </header>
  ) : (
      <div
        style={{
          position: 'relative',
          background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
          border: isModal ? 'none' : '1px solid #3b82f6',
          borderRadius: isModal ? '16px 16px 0 0' : 10,
          padding: '8px 14px',
          paddingTop: isModal ? 10 : (typeof window !== 'undefined' && window.innerWidth < 768 ? 'max(24px, calc(env(safe-area-inset-top, 10px) + 10px))' : 8),
          marginBottom: 0,
        }}
      >
        <button
          onClick={() => isModal && onClose ? onClose() : navigate('/admin/professores')}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 26,
            height: 26,
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.35)',
            background: 'rgba(255,255,255,0.16)',
            color: '#fff',
            cursor: 'pointer',
            zIndex: 1,
          }}
          title={isModal ? 'Fechar' : 'Voltar'}
          aria-label={isModal ? 'Fechar' : 'Voltar'}
        >
          {isModal ? <X size={13} /> : <ArrowLeft size={13} />}
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6, paddingRight: 36 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {hasFotoReal((professor as any).fotoUrl) ? (
              <img
                src={(professor as any).fotoUrl}
                alt={professor.nome || 'Professor'}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid rgba(255,255,255,0.55)',
                  flexShrink: 0,
                }}
              />
            ) : (
              <span
                aria-hidden
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.18)',
                  color: '#fff',
                  border: '2px solid rgba(255,255,255,0.45)',
                  flexShrink: 0,
                }}
              >
                <User size={18} />
              </span>
            )}
            <div>
              <h1 style={{ fontSize: '.92rem', fontWeight: 800, margin: 0, color: '#fff', lineHeight: 1.1 }}>{professor.nome}</h1>
              <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.68rem', margin: '1px 0 0' }}>{professor.email} {professor.telefone ? `• ${professor.telefone}` : ''}</p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              padding: '2px 8px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700,
              background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
            }}>
              <BarChart3 size={11} /> {pctAderencia}%
            </span>
            <span className={`badge ${professor.ativo ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: '0.66rem', padding: '2px 8px', background: '#ffffff', color: professor.ativo ? '#1e40af' : '#991b1b' }}>
              {professor.ativo ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        </div>
      </div>
  );

  const kpiRow = desktopFicha ? (
        <div className="resp-ficha-kpis">
          <div className="resp-ficha-kpi">
            <span className="resp-ficha-kpi-icon"><BarChart3 size={15} /></span>
            <div className="resp-ficha-kpi-main">
              <span className="resp-ficha-kpi-val" style={{ color: aderenciaColor }}>{pctAderencia}%</span>
              <span className="resp-ficha-kpi-lbl">Aderência</span>
            </div>
          </div>
          <div className="resp-ficha-kpi">
            <span className="resp-ficha-kpi-icon"><GraduationCap size={15} /></span>
            <div className="resp-ficha-kpi-main">
              <span className="resp-ficha-kpi-val">{turmasProf.length}</span>
              <span className="resp-ficha-kpi-lbl">Turmas (grade)</span>
            </div>
          </div>
          <div className="resp-ficha-kpi">
            <span className="resp-ficha-kpi-icon"><BookOpen size={15} /></span>
            <div className="resp-ficha-kpi-main">
              <span className="resp-ficha-kpi-val">{materiasSelecionadas.size}</span>
              <span className="resp-ficha-kpi-lbl">Matérias</span>
            </div>
          </div>
          <div className="resp-ficha-kpi">
            <span className="resp-ficha-kpi-icon"><ClipboardList size={15} /></span>
            <div className="resp-ficha-kpi-main">
              <span className="resp-ficha-kpi-val">{chamadasConcluidas.length}/{totalChamadas}</span>
              <span className="resp-ficha-kpi-lbl">Chamadas</span>
            </div>
          </div>
        </div>
  ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, padding: '6px 14px 6px' }}>
        <div style={{ background: 'var(--bg-card, white)', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '5px 8px', display: 'flex', alignItems: 'center', gap: 7 }}>
          <div style={{ position: 'relative', width: 28, height: 28, flexShrink: 0 }}>
            <svg viewBox="0 0 36 36" style={{ width: 28, height: 28, transform: 'rotate(-90deg)' }}>
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="4" />
              <circle cx="18" cy="18" r="15.5" fill="none" stroke={aderenciaColor} strokeWidth="4" strokeDasharray={`${pctAderencia * 0.975} 100`} strokeLinecap="round" />
            </svg>
            <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.48rem', fontWeight: 800, color: aderenciaColor }}>{pctAderencia}%</span>
          </div>
          <div>
            <div style={{ fontSize: '.54rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>Aderência</div>
            <div style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--text-main)', lineHeight: 1 }}>{pctAderencia >= 90 ? 'Excelente' : pctAderencia >= 70 ? 'Regular' : 'Atenção'}</div>
          </div>
        </div>
        <div style={{ background: 'var(--bg-card, white)', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '5px 8px', display: 'flex', alignItems: 'center', gap: 7 }}>
          <div style={{ width: 26, height: 26, borderRadius: 6, flexShrink: 0, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><GraduationCap size={13} color="#7c3aed" /></div>
          <div>
            <div style={{ fontSize: '.54rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>Turmas</div>
            <div style={{ fontSize: '.78rem', fontWeight: 800, color: '#7c3aed', lineHeight: 1 }}>{turmasProf.length}</div>
          </div>
        </div>
        <div style={{ background: 'var(--bg-card, white)', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '5px 8px', display: 'flex', alignItems: 'center', gap: 7 }}>
          <div style={{ width: 26, height: 26, borderRadius: 6, flexShrink: 0, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BookOpen size={13} color="#2563eb" /></div>
          <div>
            <div style={{ fontSize: '.54rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>Matérias</div>
            <div style={{ fontSize: '.78rem', fontWeight: 800, color: '#2563eb', lineHeight: 1 }}>{materiasSelecionadas.size}</div>
          </div>
        </div>
        <div style={{ background: 'var(--bg-card, white)', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '5px 8px', display: 'flex', alignItems: 'center', gap: 7 }}>
          <div style={{ width: 26, height: 26, borderRadius: 6, flexShrink: 0, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ClipboardList size={13} color="#16a34a" /></div>
          <div>
            <div style={{ fontSize: '.54rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>Chamadas</div>
            <div style={{ fontSize: '.78rem', fontWeight: 800, color: '#16a34a', lineHeight: 1 }}>{chamadasConcluidas.length}<span style={{ fontSize: '.55rem', fontWeight: 500, color: '#9ca3af' }}>/{totalChamadas}</span></div>
          </div>
        </div>
      </div>
  );

  const tabsNav = desktopFicha ? (
    <nav className="resp-ficha-tabs resp-ficha-tabs--scroll" aria-label="Secções da ficha">
      {TABS.map((t) => {
        const Icon = t.icon;
        return (
          <button
            key={t.key}
            type="button"
            className={`resp-ficha-tab${tab === t.key ? ' resp-ficha-tab--active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            <Icon size={12} />
            {t.label}
          </button>
        );
      })}
    </nav>
  ) : (
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border-color, #e5e7eb)', marginBottom: 2, padding: '0 14px', overflowX: 'auto' }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '4px 12px',
              fontSize: '0.78rem', fontWeight: tab === t.key ? 700 : 500,
              color: tab === t.key ? '#1B365D' : '#6b7280',
              borderBottom: tab === t.key ? '2px solid #1B365D' : '2px solid transparent',
              background: 'transparent', border: 'none', cursor: 'pointer', transition: 'all 0.15s',
            }}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>
  );

  const tabPanels = (
    <>
      {/* === TAB: Dados Pessoais === */}
      {tab === 'dados' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 14px 10px' }}>

          {/* ── Content Grid ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            {/* Left Column: Info Pessoal + Turmas */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Informações Pessoais */}
              <div style={{ background: 'var(--bg-card, white)', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '6px 8px' }}>
                <h3 style={{ fontSize: '0.72rem', fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5, margin: '0 0 4px' }}>
                  <User size={12} color="#2563eb" /> Informações Pessoais
                </h3>
                <div style={{ display: 'grid', gap: 1 }}>
                  {[
                    { label: 'Nome', value: professor.nome },
                    { label: 'E-mail', value: professor.email },
                    { label: 'CPF', value: professor.cpf ? formatCPF(professor.cpf) : '—' },
                    { label: 'Telefone', value: professor.telefone || '—' },
                    { label: 'Especialidade', value: professor.especialidade || '—' },
                    { label: 'Formação', value: (professor as any).formacao || '—' },
                    { label: 'Conta', value: String((professor as any).statusConta || 'ATIVA') },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 6px', borderRadius: 4, background: 'var(--bg-main, #f8fafc)' }}>
                      <span style={{ fontSize: '.64rem', fontWeight: 600, color: '#6b7280', minWidth: 70, flexShrink: 0 }}>{label}</span>
                      <span style={{ fontSize: '.72rem', fontWeight: 500, color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Turmas vinculadas */}
              <div style={{ background: 'var(--bg-card, white)', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '6px 8px' }}>
                <h3 style={{ fontSize: '0.72rem', fontWeight: 700, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 5, margin: '0 0 3px' }}>
                  <GraduationCap size={12} color="#7c3aed" /> Turmas Vinculadas
                </h3>
                {turmasProf.length > 0 ? (
                  <div style={{ display: 'grid', gap: 4 }}>
                    {turmasProf.map((t) => (
                      <div key={t.turmaId} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '5px 8px', borderRadius: 8,
                        background: '#faf5ff', border: '1px solid #e9d5ff',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 24, height: 24, borderRadius: 5, background: '#7c3aed', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Users size={12} />
                          </div>
                          <span style={{ fontWeight: 700, fontSize: '.76rem', color: '#581c87' }}>{t.turmaNome}</span>
                        </div>
                        <span style={{ padding: '1px 6px', borderRadius: 999, fontSize: '.62rem', fontWeight: 700, background: '#ede9fe', color: '#6d28d9' }}>{t.aulas} aula{t.aulas !== 1 ? 's' : ''}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '4px 0', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <GraduationCap size={14} style={{ opacity: 0.3 }} />
                    <span style={{ fontSize: '.68rem' }}>Nenhuma turma na grade de hoje</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Matérias */}
            <div style={{ background: 'var(--bg-card, white)', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '6px 8px' }}>
              <h3 style={{ fontSize: '0.72rem', fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5, margin: '0 0 4px' }}>
                <BookOpen size={14} color="#2563eb" /> Matérias
                <span style={{ marginLeft: 'auto', fontSize: '.62rem', fontWeight: 500, color: '#9ca3af' }}>{materiasSelecionadas.size}/{materiasVisiveis.length}</span>
              </h3>
              {materiasVisiveis.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 3 }}>
                  {materiasVisiveis.map((m) => {
                    const selected = materiasSelecionadas.has(m.id);
                    const pending = pendingMateriaId === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        disabled={!!pendingMateriaId || !canManage}
                        onClick={() => void toggleMateria(m.id)}
                        title={!canManage ? 'Acesso restrito' : selected ? `Desvincular ${m.nome}` : `Vincular ${m.nome}`}
                        style={{
                          textAlign: 'left',
                          padding: '3px 6px',
                          borderRadius: 6,
                          border: `1.5px solid ${selected ? '#3b82f6' : '#e5e7eb'}`,
                          background: selected ? 'linear-gradient(135deg, #eff6ff, #dbeafe)' : '#f8fafc',
                          color: selected ? '#1e3a8a' : '#334155',
                          cursor: !canManage ? 'default' : pendingMateriaId ? 'wait' : 'pointer',
                          transition: 'all .15s',
                          opacity: pending ? 0.7 : m.ativo ? 1 : 0.6,
                          minHeight: 30,
                        }}
                      >
                        <div style={{ fontSize: '.68rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.nome}</div>
                        <div style={{ fontSize: '.54rem', marginTop: 0, color: selected ? '#2563eb' : '#94a3b8', display: 'flex', alignItems: 'center', gap: 3 }}>
                          {pending ? (
                            <><RefreshCw size={10} /> Salvando...</>
                          ) : selected ? (
                            <><CheckCircle2 size={10} /> Vinculada</>
                          ) : (
                            'Clique para vincular'
                          )}
                          {!m.ativo ? ' • Inativa' : ''}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: 0 }}>Nenhuma matéria disponível</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* === TAB: Convite (layout compacto — só nesta página) === */}
      {tab === 'convite' && (
        <div className="ac-prof-convite-compact" style={{ padding: '0 24px 24px' }}>
          <div className="ac-prof-convite-compact__head">
            <span className="ac-prof-convite-compact__title">Convite</span>
            <span
              className={
                convitePendente
                  ? 'ac-prof-convite-compact__status ac-prof-convite-compact__status--pendente'
                  : statusConta === 'ATIVA'
                    ? 'ac-prof-convite-compact__status ac-prof-convite-compact__status--ok'
                    : 'ac-prof-convite-compact__status ac-prof-convite-compact__status--muted'
              }
            >
              {statusConta === 'ATIVA' && 'Ativa'}
              {statusConta === 'PENDENTE' && 'Pendente'}
              {statusConta === 'ATIVANDO' && 'Em ativação'}
              {statusConta === 'FALHOU' && 'E-mail falhou'}
              {statusConta === 'SUSPENSA' && 'Suspensa'}
              {statusConta === 'BLOQUEADA' && 'Bloqueada'}
              {!['ATIVA', 'PENDENTE', 'ATIVANDO', 'FALHOU', 'SUSPENSA', 'BLOQUEADA'].includes(statusConta) && (statusConta || '—')}
            </span>
            <span className="ac-prof-convite-compact__email">{professor.email}</span>
          </div>

          {convitePendente && (
            <>
              {linkExpirado && (
                <div className="ac-prof-convite-compact__warn" role="status">
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    Este convite expirou. Use <strong>Renovar link</strong> — o código, a validade e o endereço abaixo são atualizados automaticamente.
                  </span>
                </div>
              )}

              {(codigoDisplay || tokenExpiraFmt) && (
                <div className="ac-prof-convite-compact__meta">
                  {codigoDisplay && (
                    <div className="ac-prof-convite-compact__metaItem">
                      <span className="ac-prof-convite-compact__metaLabel">Código</span>
                      <code className="ac-prof-convite-compact__code">{codigoDisplay}</code>
                    </div>
                  )}
                  {tokenExpiraFmt && (
                    <div className="ac-prof-convite-compact__metaItem">
                      <span className="ac-prof-convite-compact__metaLabel">Validade</span>
                      <span className={linkExpirado ? 'ac-prof-convite-compact__expired' : undefined}>
                        {tokenExpiraFmt}
                        {linkExpirado ? ' · expirado' : ''}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {linkUtilParaEnvio ? (
                <div className="ac-prof-convite-compact__linkBlock">
                  <div className="ac-prof-convite-compact__linkLabel">
                    <Link2 size={12} /> Link de ativação
                  </div>
                  <div className="ac-prof-convite-compact__linkRow">
                    <input
                      readOnly
                      value={linkConviteExibicao}
                      className="ac-prof-convite-compact__linkInput"
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                    <button type="button" className="ac-prof-convite-compact__iconBtn" onClick={copiarLinkAtual} title="Copiar link">
                      <Copy size={14} />
                    </button>
                  </div>
                  {!gerandoLink && (
                    <button
                      type="button"
                      className="ac-prof-convite-compact__wa"
                      onClick={() => linkConviteExibicao && abrirWhatsAppComLink(linkConviteExibicao)}
                    >
                      WhatsApp
                    </button>
                  )}
                </div>
              ) : linkExpirado ? (
                <p className="ac-prof-convite-compact__hint">O endereço anterior deixou de ser aceito. Renove para obter um link válido.</p>
              ) : !linkConviteExibicao ? (
                <p className="ac-prof-convite-compact__hint">Sem token no servidor. Use o botão abaixo para gerar.</p>
              ) : null}

              <div className="ac-prof-convite-compact__toolbar">
                {canManage && <button
                  type="button"
                  className="btn-primary ac-prof-convite-compact__btnPrimary"
                  disabled={gerandoLink}
                  onClick={() => void gerarNovoLink()}
                  title="Gera novo token, copia para a área de transferência e atualiza código e validade nesta página"
                >
                  <RefreshCw size={14} />
                  {gerandoLink ? 'Atualizando…' : 'Renovar link (copiar)'}
                </button>}
              </div>
              <div style={{ marginTop: 14, background: 'var(--bg-main, #f8fafc)', padding: 16, borderRadius: 10, border: '1px solid var(--border-color, #e5e7eb)' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '0.75rem', fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Info size={14} color="#3b82f6" /> Instruções de Convite
                </h4>
                <p style={{ margin: 0, fontSize: '0.7rem', color: '#64748b', lineHeight: 1.5 }}>
                  O link de ativação é único e temporário. Envie-o para o professor via WhatsApp ou e-mail. 
                  Ao clicar no link, ele será direcionado para criar sua senha de acesso. 
                  Se o link expirar, clique em "Renovar link" para gerar um novo código de acesso seguro.
                </p>
              </div>
            </>
          )}

          {!convitePendente && statusConta === 'ATIVA' && (
            <>
              <div className="ac-prof-convite-compact__okbox">
                <CheckCircle2 size={14} className="ac-prof-convite-compact__okIcon" />
                <span>Conta ativa — convite não é mais necessário. Se ele perdeu acesso, envie recuperação de senha.</span>
                {canManage && <button
                  type="button"
                  className="ac-prof-convite-compact__btnSecondary"
                  onClick={() => void enviarRecuperacaoSenha()}
                  disabled={enviandoRecuperacao}
                  title="Envia e-mail com link/código para redefinir a senha"
                >
                  {enviandoRecuperacao ? 'Enviando…' : 'Enviar recuperação de senha'}
                </button>}
              </div>
              <div style={{ marginTop: 14, background: 'var(--bg-main, #f8fafc)', padding: 16, borderRadius: 10, border: '1px solid var(--border-color, #e5e7eb)' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '0.75rem', fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Info size={14} color="#3b82f6" /> Sobre o Acesso do Professor
                </h4>
                <p style={{ margin: 0, fontSize: '0.7rem', color: '#64748b', lineHeight: 1.5 }}>
                  O professor já realizou o primeiro acesso e sua conta está totalmente ativa. 
                  Ele pode acessar o painel utilizando seu e-mail e senha cadastrados. 
                  Caso ele não se lembre da senha, utilize o botão de recuperação acima. O link de redefinição será enviado diretamente para <strong>{professor.email}</strong>.
                </p>
              </div>
            </>
          )}

          {!convitePendente && (statusConta === 'SUSPENSA' || statusConta === 'BLOQUEADA') && (
            <div className="ac-prof-convite-compact__warn" role="alert">
              <span>
                Ajuste o status na lista de professores antes de reenviar convites.
              </span>
            </div>
          )}
        </div>
      )}

      {/* === TAB: Aderência === */}
      {tab === 'aderencia' && (
        <div style={{ padding: '0 24px 24px' }}>
          <div style={{ marginBottom: 16, background: 'var(--bg-main, #f8fafc)', padding: 16, borderRadius: 10, border: '1px solid var(--border-color, #e5e7eb)' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '0.75rem', fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Info size={14} color="#3b82f6" /> Registro de Chamadas
            </h4>
            <p style={{ margin: 0, fontSize: '0.7rem', color: '#64748b', lineHeight: 1.5 }}>
              Abaixo estão listadas as chamadas mais recentes registradas por este professor. 
              O sistema acompanha automaticamente a taxa de aderência e a frequência de preenchimento do diário de classe.
              Chamadas marcadas como <strong style={{ color: '#d97706' }}>Pendentes</strong> indicam que a aula ocorreu, mas o professor ainda não finalizou o registro de presença no aplicativo.
            </p>
          </div>

          {/* Tabela de chamadas recentes */}
          <div style={{ background: 'var(--bg-card, white)', borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)', overflow: 'hidden' }}>
            <div className="dt-container"><table className="dt-table" style={{ width: '100%' }}>
              <thead>
                <tr><th>Data</th><th>Turma</th><th>Matéria</th><th>Status</th><th>Presentes</th><th>Ausentes</th></tr>
              </thead>
              <tbody>
                {chamadasRecentes.length === 0 ? (
                  <tr><td colSpan={6} className="dt-empty">Nenhuma chamada registrada</td></tr>
                ) : chamadasRecentes.map((c) => {
                  const formatDate = (d?: string) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; } };
                  return (
                    <tr key={c.id} className="dt-row">
                      <td style={{ fontSize: '0.82rem' }}>{formatDate(c.data || (c as any).dataReferencia)}</td>
                      <td style={{ fontWeight: 500 }}>{c.turma?.nome || '—'}</td>
                      <td style={{ color: '#6b7280' }}>{c.materia?.nome || '—'}</td>
                      <td>
                        <span style={{
                          padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 700,
                          background: c.status === 'CONCLUIDA' ? '#dcfce7' : '#fef3c7',
                          color: c.status === 'CONCLUIDA' ? '#166534' : '#92400e',
                        }}>{c.status}</span>
                      </td>
                      <td style={{ color: '#16a34a', fontWeight: 600 }}>{c.totalPresentes ?? '—'}</td>
                      <td style={{ color: '#dc2626', fontWeight: 600 }}>{c.totalAusentes ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
        </div>
      )}

    </>
  );

  return (
    <div
      className={desktopFicha ? 'resp-ficha' : isModal ? '' : 'admin-page'}
      style={{
        marginTop: desktopFicha ? 0 : isModal ? 0 : (typeof window !== 'undefined' && window.innerWidth < 768 ? 0 : -24),
        ...(desktopFicha || isModal ? { display: 'flex', flexDirection: 'column' as const, flex: desktopFicha ? 1 : undefined, minHeight: desktopFicha ? 0 : undefined } : {}),
      }}
    >
      {headerBlock}
      {desktopFicha ? (
        <div className="resp-ficha-body">
          <div className="resp-ficha-stack">
            {kpiRow}
            {tabsNav}
            <div className="resp-ficha-panel">{tabPanels}</div>
          </div>
        </div>
      ) : (
        <>
          {kpiRow}
          {tabsNav}
          {tabPanels}
        </>
      )}

      {/* Footer legado (modal antigo) */}
      {isModal && !desktopFicha && (
        <div className="ac-ficha-actions" style={{ marginTop: 'auto', position: 'sticky', bottom: 0, zIndex: 5, borderRadius: '0 0 16px 16px' }}>
          <span className="ac-ficha-created">
            {professor.criadoEm ? `Cadastrado em ${new Date(professor.criadoEm).toLocaleDateString('pt-BR')}` : '—'}
          </span>
          <span className="ac-ficha-action-spacer" />
          {canManage && <button type="button" className="ac-ficha-action-btn ac-ficha-action-btn--edit" onClick={() => onEdit?.()}>
            <Pencil size={13} /> Editar Dados
          </button>}
          {professor.ativo ? (
            canManage && <button type="button" className="ac-ficha-action-btn ac-ficha-action-btn--deactivate" onClick={() => onDeactivate?.()}>
              <PowerOff size={13} /> Desativar
            </button>
          ) : (
            canManage && <button type="button" className="ac-ficha-action-btn ac-ficha-action-btn--reactivate" onClick={() => onDeactivate?.()}>
              <RotateCcw size={13} /> Reativar
            </button>
          )}
        </div>
      )}
    </div>
  );
}
